import { FileDownloadService } from './../../../shared/file-download/file-download.service';
import { CgpApiService } from './../../../../modules/cgp-api';
import { Injectable } from '@angular/core';
import { BaseHttpService } from 'src/app/shared/base-http/base-http.service';
import { forkJoin, Observable } from 'rxjs';
import { PageOptions } from 'src/app/shared/data-table/data-table.interface';
import { config } from 'src/config/config';
import { getEnv } from 'src/modules/utility';
import { CgpSnackbarService } from 'src/app/shared/cgp-snackbar/cgp-snackbar.service';

@Injectable()
export class EcmService {

   constructor(
      private readonly baseHttpService: BaseHttpService,
      private readonly cgpApiService: CgpApiService,
      private readonly snackbar: CgpSnackbarService,
      private readonly download: FileDownloadService
   ) { }

   public getAllTools(body, pageOptions?: PageOptions): Observable<any> {
      if (!pageOptions) {
         pageOptions = {
            page: 0,
            order: 'asc',
            size: 10,
            sort: 'toolId',
         };
      }

      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/tool?page=${pageOptions.page}&size=${pageOptions.size}&sort=${pageOptions.sort}&order=${pageOptions.order}`;
      return this.baseHttpService.post(url, body);
   }

   public getAuthorities(toolChangeId: number, pageOptions?: PageOptions) {
      pageOptions = pageOptions || {
         page: 0,
         order: 'asc',
         size: 9999, // TODO: Replace this with service call that doesn't have a limit for all
         sort: 'authNumber',
      };

      const params = this.baseHttpService.generateHttpParams({
         page: pageOptions.page,
         size: pageOptions.size,
         sort: pageOptions.sort,
         order: pageOptions.order,
         toolChangeID: toolChangeId
      });
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/authorities`;
      return this.baseHttpService.get(url, { params });
   }

   public getAuthorityNumbers() {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/criteria/auth-number`;
      return this.baseHttpService.get(url);
   }

   public getAllPrograms() {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/criteria/program-name`;
      return this.baseHttpService.get(url);
   }

   public getProgramsUsedInEcm() {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/criteria/program-name-in-use`;
      return this.baseHttpService.get(url);
   }

   /**
    * Retrieve all programs that has been added to TPRECM.
    * This is the same as `getAllPrograms` but just returns an array of objects that
    * has `id` and `programNameYear`
    *
    * @param {PageOptions} [pageOptions]
    * @memberof EcmService
    */
   public getAllProgramNames() {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/criteria/program-name`;
      return this.baseHttpService.get(url);
   }

   public getEcmNec(toolId: number) {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}tpr/nec?toolId=${toolId}`;
      return this.baseHttpService.get(url);
   }

   public getParts(toolId?: number, programId?: number, partNumber?: string, pageOptions?: PageOptions, filterColumn: any = {}) {
      pageOptions = pageOptions || {
         page: 0,
         order: 'asc',
         size: 10,
         sort: 'partNumber',
      };

      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part/grid?page=${pageOptions.page}&size=${pageOptions.size}&sort=${pageOptions.sort}&order=${pageOptions.order}`;
      return this.baseHttpService.post(url, { toolId, programId, partNumber, filterColumn });
   }

   public getPartsByTool(toolId: number, pageOptions?: PageOptions) {
      return this.getParts(toolId, undefined, undefined, pageOptions);
   }

   public getPartsByProgram(toolId: number, programId: number, searchPartNumber: string, pageOptions?: PageOptions) {
      return this.getPartsForProgram(toolId, programId, searchPartNumber, pageOptions);
   }

   public updateEcmNecCompleteDate(toolId: number, necLec: string, date: Date) {
      const dto = {
         toolId,
         necLec,
         necCompleteDate: date
      };

      const url = `${config.tprecmApiBaseUrl[getEnv()]}tpr/nec`;
      return this.baseHttpService.put(url, dto);
   }

   public findToolsByCriteria(sourceId: number, programId: number, workerId: number, reSourced) {
      const dto = {
         sourceId,
         programId,
         workerId,
         reSourced
      };

      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/criteria/tool`;
      return this.baseHttpService.post(url, dto);
   }

   public getDesignSourceCriteria() {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}tpr/criteria/design-source`;
      return this.baseHttpService.get(url);
   }

   public getProgramCriteria() {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/criteria/program-name`;
      return this.baseHttpService.get(url);
   }

   public getEngineerCriteria() {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}tpr/criteria/mse`;
      return this.baseHttpService.get(url);
   }

   public getAuthTypeCriteria() {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/criteria/auth-type`;
      return this.baseHttpService.get(url);
   }

   public getToolPartRelations(body, pageOptions?: PageOptions) {
      if (!pageOptions) {
         pageOptions = {
            page: 0,
            order: 'asc',
            size: 10,
            sort: 'toolId',
         };
      }

      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/tool/part-relation`
         + `?page=${pageOptions.page}&size=${pageOptions.size}&sort=${pageOptions.sort}&order=${pageOptions.order}`;
      return this.baseHttpService.post(url, body);
   }

   public updateToolPartRelations(body: any[]) {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/tool/part`;
      return this.baseHttpService.put(url, body);
   }

   public deleteToolPartRelationship(partToToolId: number, partId: number, toolId: number) {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/tool/part/${partToToolId}/${partId}/${toolId}`;
      return this.baseHttpService.delete(url);
   }

   public createToolPartRelationship(body: any[]) {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/tool/part`;
      return this.baseHttpService.post(url, body);
   }

   public getNecLec(toolId: number) {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/nec-lec?toolId=${toolId}`;
      return this.baseHttpService.get(url);
   }

   public updateEcmTool(body: any) {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/tool`;
      return this.baseHttpService.put(url, body);
   }

   public getEcmTool(partId: any) {
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/tool?partId=${partId}`;
      return this.baseHttpService.get(url);
   }
   /**
    * Prints the specified NEC Report to a file
    *
    * @param {number} toolId
    * @param {string} necLec
    * @memberof EcmPartsService
    */
   public printNecReport(toolId: number, necLec: string, saveFile: boolean, tprToolId?: number, toolNo?: string, programName?: string, reportTag?: string) {
      const date = new Date().toLocaleDateString().replace(/\//g, '-');
      this.download.downloadFileWithBody(
         `${programName || ''}_${toolNo}_${reportTag}_NEC_${necLec}_${date}.pdf`,
         'ecm/report/nec/download',
         { toolId, necLec, saveFile, tprToolId }
      ).subscribe();
   }

   /**
    * Print the ECM report for a tool
    *
    * @param {number} toolId
    * @memberof EcmService
    */
   printEcmReport(toolId: number) {
      const ref = this.snackbar.showSnackbar({
         message: 'Downloading...',
         action: 'Dismiss',
         duration: null
      });

      this.download.downloadFile(
         `ecm/report/download?toolId=${toolId}`
      ).subscribe(() => ref.dismiss());
   }

   printEcmReports(toolIds: number[]) {
      const ref = this.snackbar.showSnackbar({
         message: 'Downloading...',
         action: 'Dismiss',
         duration: null
      });

      forkJoin(
         toolIds.map(id => this.download.downloadFile(`ecm/report/download?toolId=${id}`))
      ).subscribe(() => ref.dismiss());
   }

   public uploadParts(toolId: number, programId: number, file: File) {
      const form = new FormData();
      form.append('file', file);

      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-file?programId=${programId}&toolId=${toolId}`;
      return this.cgpApiService.multipartPost(url, form, null);
   }


   private getPartsForProgram(toolId?: number, programId?: number, partNumber?: string, pageOptions?: PageOptions) {
      pageOptions = pageOptions || {
         page: 0,
         order: 'asc',
         size: 10,
         sort: 'partNumber',
      };
      const partNumberFilterParam = partNumber !== undefined ? '&partNumberFilter=' + `${partNumber}` : '';
      const programFilterParam = programId !== null ? '&programId=' + `${programId}` : '';
      const url = `${config.tprecmApiBaseUrl[getEnv()]}ecm/part-by-program` +
         `?page=${pageOptions.page}&size=${pageOptions.size}&sort=${pageOptions.sort}&order=${pageOptions.order}&ecmToolId=${toolId}${programFilterParam}${partNumberFilterParam}`;

      return this.baseHttpService.get(url);
   }

}
